import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Brain, Mic, BookOpen, TrendingUp, Target, Zap, CheckCircle2 } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const capabilities = [
  { icon: Target,    title: "Proficiency Assessment",      description: "Automatic CEFR-aligned assessment (A1–C2) based on natural conversation — no standardized tests required." },
  { icon: Mic,       title: "Speech Analysis",             description: "Real-time pronunciation scoring, fluency detection, and accent guidance to help learners sound more natural." },
  { icon: BookOpen,  title: "Vocabulary Intelligence",     description: "AI identifies vocabulary gaps and deploys spaced-repetition exercises exactly when learners are ready." },
  { icon: Brain,     title: "Grammar Coaching",            description: "Contextual grammar correction with explanations delivered in the learner's native language." },
  { icon: TrendingUp,title: "Learning Path Optimization",  description: "Adaptive algorithms adjust each learner's program weekly, maximizing efficiency." },
  { icon: Zap,       title: "Session Summaries",           description: "After every session, learners receive an AI-generated summary with achievements and next steps." },
];

export default function AIFeatures() {
  return (
    <div className="min-h-screen bg-background">

      <section className="pt-28 pb-20 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">AI Technology</p>
              <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
                AI That Makes Every Conversation Count
              </h1>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                Lingua2's AI was built specifically for language learning — not repurposed from a general model. It understands the nuances of real human communication.
              </p>
              <div className="flex gap-3">
                <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
                  <Link to="/contact">See AI in Action <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="transition-fast">
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl-token">
              <img
                src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="AI language technology"
                className="w-full h-[360px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="AI Capabilities" title="Six Ways AI Accelerates Learning" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((c) => (
              <div key={c.title} className="group p-6 rounded-xl border border-border bg-card card-hover hover:border-primary/25">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/14 transition-fast">
                  <c.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="text-sm font-display font-semibold text-foreground mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Contact us <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CEFR */}
      <section className="py-20 bg-surface">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Standards" title="CEFR-Aligned Assessment" description="All assessments in Lingua2 align with the Common European Framework of Reference for Languages." />
          <div className="space-y-2.5">
            {[
              { level: "A1", name: "Beginner",           desc: "Basic phrases and expressions",               bg: "bg-red-50 text-red-600" },
              { level: "A2", name: "Elementary",         desc: "Simple communication in familiar situations",   bg: "bg-orange-50 text-orange-600" },
              { level: "B1", name: "Intermediate",       desc: "Main points on familiar topics",               bg: "bg-yellow-50 text-yellow-700" },
              { level: "B2", name: "Upper Intermediate",  desc: "Complex texts and spontaneous interaction",    bg: "bg-lime-50 text-lime-700" },
              { level: "C1", name: "Advanced",           desc: "Complex, demanding language tasks",            bg: "bg-green-50 text-green-700" },
              { level: "C2", name: "Mastery",            desc: "Full professional and academic proficiency",   bg: "bg-teal-50 text-teal-700" },
            ].map((l) => (
              <div key={l.level} className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card">
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${l.bg}`}>
                  {l.level}
                </span>
                <div className="flex-1">
                  <span className="text-sm font-semibold text-foreground">{l.name}</span>
                  <span className="text-sm text-muted-foreground ml-2">— {l.desc}</span>
                </div>
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button size="lg" asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Contact us<ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
